import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material'
import React, { useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { StatusCheckNameField } from 'components/status-checks/form/status-check/common/StatusCheckNameField'
import { DatadogMonitorSearch } from 'components/status-checks/form/status-check/datadog/DatadogMonitorSearch'
import { DatadogMonitorURL } from 'components/status-checks/form/status-check/datadog/DatadogMonitorURL'
import {
  type StatusCheckFormValues,
  StatusCheckMonitorSelectionRadioValue
} from 'components/status-checks/status-checks-form-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { DATADOG_MONITOR_URL_PREFIX } from 'utils/status-checks/status-check-integrations-util-datadog'

export const DatadogMonitorSelection: React.FC = () => {
  const [selectedValue, setSelectedValue] = useState<string | undefined>()
  const { serviceName } = useStatusCheckDetailsContext()
  const { control, setValue, trigger } = useFormContext<StatusCheckFormValues>()
  const [domain, externalIntegration] = useWatch<StatusCheckFormValues>({
    control,
    name: ['domain', 'externalIntegration']
  })

  const [radioValue, setRadioValue] =
    useState<StatusCheckMonitorSelectionRadioValue>(
      serviceName
        ? StatusCheckMonitorSelectionRadioValue.SEARCH
        : StatusCheckMonitorSelectionRadioValue.URL
    )

  return (
    <Stack sx={{ rowGap: 3 }}>
      <StatusCheckNameField />
      <Stack sx={{ rowGap: 1 }}>
        <Typography>
          What monitor or alert will help Gremlin determine the health of your
          Service?
        </Typography>
        <RadioGroup
          name="monitor-selection"
          sx={{ marginLeft: 1, rowGap: 1 }}
          value={radioValue}
          onChange={(_, value: StatusCheckMonitorSelectionRadioValue) =>
            setRadioValue(value)
          }
        >
          <FormControl>
            <FormControlLabel
              control={<Radio />}
              label="Datadog monitor URL"
              value={StatusCheckMonitorSelectionRadioValue.URL}
            />
            <Box sx={{ marginLeft: 4 }}>
              <DatadogMonitorURL radioValue={radioValue} />
            </Box>
          </FormControl>
          <FormControl>
            <FormControlLabel
              control={<Radio />}
              label="Search for Datadog monitor by name"
              value={StatusCheckMonitorSelectionRadioValue.SEARCH}
            />
            <Box sx={{ marginLeft: 4 }}>
              <DatadogMonitorSearch
                disabled={
                  radioValue !== StatusCheckMonitorSelectionRadioValue.SEARCH
                }
                domain={domain?.name}
                initialSearchValue={serviceName}
                integrationName={externalIntegration.name}
                selectedValue={selectedValue}
                onSelect={async (newValue) => {
                  setSelectedValue(newValue)
                  setValue(
                    'monitorUrl',
                    `${externalIntegration.url}${DATADOG_MONITOR_URL_PREFIX}${newValue}`,
                    { shouldDirty: true } // Required to advance through edit workflow
                  )
                  await trigger('monitorUrl')
                }}
              />
            </Box>
          </FormControl>
        </RadioGroup>
      </Stack>
    </Stack>
  )
}
